import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ArrowDown, ArrowUp, Plus, Trash2, X } from 'lucide-react';
import { backdropClose } from '../../utils/backdropClose';
import { staffApi, StaffLevel, TONE_CLASS } from './staffApi';

const ROLES = [
  { id: 'trainer', label: 'Trainers', examples: 'Coach · Senior Coach · Master Coach' },
  { id: 'therapist', label: 'Therapists', examples: 'Therapist · Senior Therapist' },
  { id: 'consultant', label: 'Consultants', examples: 'Consultant · Lead Consultant' },
];
const TONES = Object.keys(TONE_CLASS);

/**
 * Seniority levels per role (priced per offering: a Senior's PT plan can cost
 * more than a Coach's), plus the gym's staff settings — whether providers may
 * edit their own profile and hours, and whether members see gender.
 */
export default function StaffLevelsModal({ gymId, onClose }: { gymId: string; onClose: () => void }) {
  const [levels, setLevels] = useState<StaffLevel[] | null>(null);
  const [settings, setSettings] = useState<{ selfEdit: boolean; showGender: boolean } | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const load = () => {
    staffApi<StaffLevel[]>(`/levels?gymId=${gymId}`).then(setLevels).catch(e => toast.error(e.message));
    staffApi<{ selfEdit: boolean; showGender: boolean }>(`/settings?gymId=${gymId}`).then(setSettings).catch(() => {});
  };
  useEffect(load, [gymId]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async (role: string) => {
    const name = (drafts[role] ?? '').trim();
    if (!name) return;
    try {
      await staffApi('/levels', { method: 'POST', body: { gymId, role, name } });
      setDrafts(d => ({ ...d, [role]: '' }));
      load();
    } catch (e: any) { toast.error(e.message); }
  };
  const patch = async (id: string, body: any) => {
    try { await staffApi(`/levels/${id}`, { method: 'PATCH', body }); load(); } catch (e: any) { toast.error(e.message); }
  };
  const remove = async (l: StaffLevel) => {
    if (l.staffCount && !window.confirm(`${l.staffCount} team member(s) have this level. Remove it? Their level prices fall back to the base price.`)) return;
    try { await staffApi(`/levels/${l.id}`, { method: 'DELETE' }); load(); } catch (e: any) { toast.error(e.message); }
  };
  const move = (list: StaffLevel[], i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    // Swap ranks (higher rank = more senior).
    patch(list[i].id, { rank: list[j].rank });
    patch(list[j].id, { rank: list[i].rank });
  };
  const setSetting = async (key: 'selfEdit' | 'showGender', value: boolean) => {
    try {
      const s = await staffApi<{ selfEdit: boolean; showGender: boolean }>('/settings', { method: 'PUT', body: { gymId, staff: { [key]: value } } });
      setSettings(s);
    } catch (e: any) { toast.error(e.message); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" {...backdropClose(onClose)}>
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100 sticky top-0 bg-white">
          <div>
            <div className="text-lg font-black text-zinc-900">Levels & coach settings</div>
            <div className="text-xs text-zinc-500">Levels set the price members pay for each coach.</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-zinc-100" aria-label="Close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-6">
          {ROLES.map(role => {
            const list = (levels ?? []).filter(l => l.role === role.id).sort((a, b) => a.rank - b.rank);
            return (
              <section key={role.id} className="space-y-2">
                <div className="flex items-baseline justify-between">
                  <h3 className="text-sm font-black text-zinc-900">{role.label}</h3>
                  <span className="text-[11px] text-zinc-400">e.g. {role.examples}</span>
                </div>
                {list.map((l, i) => (
                  <div key={l.id} className="flex items-center gap-2">
                    <div className="flex flex-col">
                      <button onClick={() => move(list, i, -1)} disabled={i === 0} className="p-0.5 text-zinc-400 hover:text-zinc-900 disabled:opacity-20" aria-label="Less senior"><ArrowUp className="w-3.5 h-3.5" /></button>
                      <button onClick={() => move(list, i, 1)} disabled={i === list.length - 1} className="p-0.5 text-zinc-400 hover:text-zinc-900 disabled:opacity-20" aria-label="More senior"><ArrowDown className="w-3.5 h-3.5" /></button>
                    </div>
                    <input
                      defaultValue={l.name}
                      onBlur={e => e.target.value.trim() && e.target.value.trim() !== l.name && patch(l.id, { name: e.target.value.trim() })}
                      className="flex-1 px-3 py-2 border border-zinc-200 rounded-xl text-sm font-semibold outline-none focus:border-zinc-900"
                    />
                    <div className="flex gap-1">
                      {TONES.map(t => (
                        <button key={t} onClick={() => patch(l.id, { tone: t })} className={`w-6 h-6 rounded-full ${TONE_CLASS[t].split(' ')[0]} ${l.tone === t ? 'ring-2 ring-offset-2 ring-zinc-900' : ''}`} aria-label={`Badge ${t}`} />
                      ))}
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${TONE_CLASS[l.tone] ?? TONE_CLASS.ink}`}>{l.name}</span>
                    <span className="text-[11px] text-zinc-400 w-14 text-right">{l.staffCount ?? 0} staff</span>
                    <button onClick={() => remove(l)} className="p-1.5 text-zinc-400 hover:text-red-500" aria-label="Remove level"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <input
                    value={drafts[role.id] ?? ''}
                    onChange={e => setDrafts(d => ({ ...d, [role.id]: e.target.value }))}
                    onKeyDown={e => e.key === 'Enter' && add(role.id)}
                    placeholder={list.length ? 'Add a more senior level' : 'Add the first level'}
                    className="flex-1 px-3 py-2 border border-dashed border-zinc-300 rounded-xl text-sm outline-none focus:border-zinc-900"
                  />
                  <button onClick={() => add(role.id)} className="inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-zinc-900 text-white text-sm font-bold"><Plus className="w-4 h-4" /> Add</button>
                </div>
              </section>
            );
          })}

          {settings && (
            <section className="space-y-3 pt-4 border-t border-zinc-100">
              <h3 className="text-sm font-black text-zinc-900">Settings</h3>
              <Setting
                on={settings.selfEdit}
                onChange={v => setSetting('selfEdit', v)}
                title="Team members edit their own profile and hours"
                body="Anyone with login access can update their photo, bio and working hours. Level and prices stay with admins."
              />
              <Setting
                on={settings.showGender}
                onChange={v => setSetting('showGender', v)}
                title="Let members filter coaches by gender"
                body="Adds a gender field to coach profiles and a filter to the coach step."
              />
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

function Setting({ on, onChange, title, body }: { on: boolean; onChange: (v: boolean) => void; title: string; body: string }) {
  return (
    <button onClick={() => onChange(!on)} className="w-full flex items-start justify-between gap-4 text-left">
      <span>
        <span className="block text-sm font-bold text-zinc-900">{title}</span>
        <span className="block text-xs text-zinc-500">{body}</span>
      </span>
      <span className={`relative shrink-0 w-11 h-6 rounded-full transition-colors ${on ? 'bg-emerald-500' : 'bg-zinc-300'}`}>
        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${on ? 'left-[22px]' : 'left-0.5'}`} />
      </span>
    </button>
  );
}
